/**
 * Detects if the current user agent is a web crawler/bot
 * Used to suppress modals and popups for better SEO and performance scores
 */

const CRAWLER_USER_AGENTS = [
  // Google
  'Googlebot',
  'Google-InspectionTool',
  'GoogleOther',
  'AdsBot-Google',
  'Mediapartners-Google',

  // Bing
  'bingbot',
  'BingPreview',
  'msnbot',

  // Other major crawlers
  'Slurp', // Yahoo
  'DuckDuckBot', // DuckDuckGo
  'Baiduspider', // Baidu
  'YandexBot', // Yandex
  'facebookexternalhit', // Facebook
  'Twitterbot', // Twitter
  'LinkedInBot', // LinkedIn
  'WhatsApp', // WhatsApp
  'Applebot', // Apple

  // SEO/Performance tools
  'Lighthouse', // Google Lighthouse
  'GTmetrix', // GTmetrix
  'PageSpeed', // PageSpeed Insights
  'Pingdom', // Pingdom
  'UptimeRobot', // Uptime monitoring
  'StatusCake', // Status monitoring

  // Generic patterns
  'bot',
  'Bot',
  'spider',
  'Spider',
  'crawler',
  'Crawler',
  'scraper',
  'Scraper',
];

/**
 * Checks if the current user agent appears to be a web crawler
 * @returns true if crawler detected, false otherwise
 */
export const isCrawler = (): boolean => {
  // Handle server-side rendering where navigator might not be available
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return false;
  }

  const userAgent = navigator.userAgent;

  // Check for headless browser indicators (common in crawlers)
  if (userAgent.includes('HeadlessChrome') || userAgent.includes('PhantomJS')) {
    return true;
  }

  // Check against known crawler user agents
  return CRAWLER_USER_AGENTS.some(crawlerPattern => userAgent.includes(crawlerPattern));
};

/**
 * Alternative detection method that also checks for missing features
 * that are often absent in headless browsers used by crawlers
 */
export const isLikelyCrawler = (): boolean => {
  if (isCrawler()) {
    return true;
  }

  // Additional heuristics for headless browsers
  if (typeof window !== 'undefined') {
    // Check for missing features common in headless browsers
    const windowAny = window as any;
    const suspiciousFeatures = [
      !windowAny.chrome && !windowAny.safari, // Missing browser-specific objects
      navigator.webdriver === true, // WebDriver flag
      navigator.plugins.length === 0, // No plugins
    ];

    // If multiple suspicious features, likely a crawler
    return suspiciousFeatures.filter(Boolean).length >= 2;
  }

  return false;
};
